import api from "../api/api";
const BASE_URL = import.meta.env.VITE_APP_API_URL;

export const expedienteAlteracaoHorarioManualService = {
  /**
   * Busca registros por filtros (incluindo status)
   * @param {Object} filtros - Filtros de busca
   * @param {number} [filtros.id_abertura] - ID da abertura (opcional)
   * @param {number} [filtros.id_funcionario] - ID do funcionário (opcional)
   * @param {string} [filtros.status] - Status do registro (opcional)
   * @param {boolean} [filtros.incluirFechados] - Incluir registros fechados (opcional)
   * @returns {Promise<Object>} Resultado da busca
   */
  async buscarRegistros(filtros = {}) {
    try {
      const params = new URLSearchParams();
      
      params.append('incluir_fechados', filtros.incluirFechados || false);
      
      if (filtros.id_abertura) params.append('id_abertura', filtros.id_abertura);
      if (filtros.id_funcionario) params.append('id_funcionario', filtros.id_funcionario);
      if (filtros.status) params.append('status', filtros.status);
      
      const url = `${BASE_URL}/expediente-ajuste/registros?${params.toString()}`;
      const response = await api.get(url);

      if (response.status === 200 && response.data.success) {
        return {
          success: true,
          registros: response.data.registros || [],
          totais: response.data.totais || {
            registros: 0,
            abertos: 0,
            fechados: 0,
            encerrados: 0
          }
        };
      }
      throw new Error(response.data?.message || "Falha ao buscar registros");
    } catch (error) {
      console.error("Erro ao buscar registros:", error);
      return {
        success: false,
        message: this.handleError(error, "buscar registros").message,
        registros: [],
        totais: {},
        error: error.response?.data
      };
    }
  },

  /**
   * Ajusta o horário de múltiplos registros
   * @param {Object} dados - Dados do ajuste
   * @param {Array<number>} dados.ids_registros - IDs dos registros a ajustar
   * @param {string} [dados.novo_horario_inicio] - Nova data/hora de início no formato ISO
   * @param {string} [dados.novo_horario_fim] - Nova data/hora de fim no formato ISO
   * @param {string} [dados.novo_horario] - Nova data/hora no formato ISO
   * @param {string} dados.motivo - Motivo do ajuste
   * @param {string} dados.usuario - Usuário responsável
   * @returns {Promise<Object>} Resultado da operação
   */
  async ajustarHorarios(dados) {
    try {
      // Validação completa antes de enviar
      if (!dados.ids_registros || !Array.isArray(dados.ids_registros) || dados.ids_registros.length === 0) {
        throw new Error("Nenhum registro selecionado para ajuste");
      }
      
      // Validar que pelo menos um horário foi informado
      const temInicio = dados.novo_horario_inicio && dados.novo_horario_inicio.trim().length > 0
      const temFim = dados.novo_horario_fim && dados.novo_horario_fim.trim().length > 0
      const temHorarioUnico = dados.novo_horario && dados.novo_horario.trim().length > 0
      
      if (!temInicio && !temFim && !temHorarioUnico) {
        throw new Error("Horário inválido ou não informado");
      }

      if (!dados.motivo || dados.motivo.trim().length < 5) {
        throw new Error("Motivo deve ter pelo menos 5 caracteres");
      }

      // Formata dados para o backend
      const payload = {
        ids_registros: dados.ids_registros,
        motivo: dados.motivo,
        usuario: dados.usuario || "Sistema"
      };
      
      // Adicionar horários conforme informado
      // Se já vier no formato MySQL (YYYY-MM-DD HH:MM:SS), usa direto
      // Se vier em outro formato, mantém como está
      if (dados.novo_horario_inicio) {
        payload.novo_horario_inicio = dados.novo_horario_inicio
      }
      if (dados.novo_horario_fim) {
        payload.novo_horario_fim = dados.novo_horario_fim
      }
      if (dados.novo_horario) {
        payload.novo_horario = dados.novo_horario
      }
      
      // ✅ Adicionar flag de atualizar fechamento geral
      if (dados.atualizar_fechamento_geral !== undefined) {
        payload.atualizar_fechamento_geral = dados.atualizar_fechamento_geral
      }
      
      console.log('📦 Payload montado no serviço:', payload)

      const response = await api.patch(`${BASE_URL}/expediente-ajuste/ajustar-horarios`, payload);
      
      if (response.status === 200 && response.data.success) {
        return {
          success: true,
          message: response.data.message || "Horários ajustados com sucesso",
          registrosAfetados: response.data.registros_afetados || 0,
          aberturaAtualizada: response.data.abertura_atualizada || null,
          logId: response.data.log_id || null,
          detalhes: response.data.detalhes || {}
        };
      }
      throw new Error(response.data?.message || "Falha no processamento do ajuste");
    } catch (error) {
      console.error("Erro detalhado ao ajustar horários:", {
        error: error.response?.data || error.message,
        payload: dados,
        status: error.response?.status
      });
      
      // Se for erro 422, mostrar detalhes específicos da validação
      if (error.response?.status === 422) {
        const validationErrors = error.response.data?.errors || {};
        const errorMessages = Object.values(validationErrors).flat();
        
        return {
          success: false,
          message: `Erro de validação: ${errorMessages.join(', ')}`,
          errorDetails: error.response?.data,
          code: error.response?.status,
          validationErrors: validationErrors
        };
      }
      
      return {
        success: false,
        message: this.handleError(error, "ajustar horários").message,
        errorDetails: error.response?.data,
        code: error.response?.status
      };
    }
  },

  /**
   * Obtém os status disponíveis com metadados
   * @returns {Promise<Object>} Lista de status disponíveis
   */

 async buscarStatusDisponiveis() {
  try {
    const response = await api.get(`${BASE_URL}/expediente-ajuste/status`);
    
    // Verificação mais robusta da resposta
    if (response.status === 200 && response.data && Array.isArray(response.data)) {
      return {
        success: true,
        status: response.data.map(item => ({
          valor: item.value || item.id,
          descricao: item.description || item.nome,
          podeEditar: item.editable !== false, // default true
          cor: item.color || this.getCorPadrao(item.value),
          ordem: item.order || 0
        }))
      };
    }
    
    // Se a resposta não for no formato esperado, retorna valores padrão
    return {
      success: true,
      status: this.getStatusPadrao()
    };
    
  } catch (error) {
    console.error("Erro ao buscar status:", error);
    return {
      success: false,
      status: this.getStatusPadrao(),
      message: this.handleError(error, "buscar status").message
    };
  }
},


// Métodos auxiliares
getStatusPadrao() {
  return [
    { valor: 'ABERTO', descricao: 'Aberto', podeEditar: true, cor: 'primary' },
    { valor: 'FECHADO', descricao: 'Fechado', podeEditar: true, cor: 'warning' },
    { valor: 'ENCERRADO', descricao: 'Encerrado', podeEditar: false, cor: 'success' }
  ];
},

getCorPadrao(status) {
  switch(status?.toUpperCase()) {
    case 'ABERTO': return 'primary';
    case 'FECHADO': return 'warning';
    case 'ENCERRADO': return 'success';
    default: return 'secondary';
  }
}
,
  
  /**
   * Verifica se um registro pode ser editado
   * @param {Object} registro - Registro a ser verificado
   * @returns {Object} Resultado da verificação
   */
  async verificarEditavel(registro) {
    try {
      if (!registro || !registro.id) {
        throw new Error("Registro inválido");
      }

      const response = await api.get(`${BASE_URL}/expediente-ajuste/verificar-editavel/${registro.id}`);
      
      if (response.status === 200 && response.data.success) {
        return {
          podeEditar: response.data.editavel || false,
          motivo: response.data.motivo || "",
          restricoes: response.data.restricoes || []
        };
      }
      throw new Error(response.data?.message || "Falha na verificação");
    } catch (error) {
      console.error("Erro ao verificar editável:", error);
      return {
        podeEditar: false,
        motivo: this.handleError(error, "verificar registro").message,
        restricoes: []
      };
    }
  },

  /**
   * Tratamento centralizado de erros
   * @private
   * @param {Error} error - Erro capturado
   * @param {string} context - Contexto da operação
   * @returns {Object} Objeto padronizado de erro
   */
  handleError(error, context = "") {
    const defaultMessage = `Erro ao ${context || "processar solicitação"}`;
    
    // Erro de resposta da API
    if (error.response) {
      const status = error.response.status;
      let message = error.response.data?.message || defaultMessage;
      
      // Mensagens personalizadas por status
      if (status === 400) message = "Dados inválidos fornecidos";
      if (status === 401) message = "Acesso não autorizado";
      if (status === 403) message = "Você não tem permissão para esta ação";
      if (status === 404) message = "Recurso não encontrado";
      if (status === 500) message = "Erro interno no servidor";
      
      return {
        success: false,
        message,
        code: status,
        details: error.response.data?.errors,
        stack: process.env.NODE_ENV === 'development' ? error.stack : undefined
      };
    }
    
    // Erro de conexão
    if (error.request) {
      return {
        success: false,
        message: "Erro de comunicação com o servidor",
        code: 0,
        details: "Verifique sua conexão com a internet"
      };
    }
    
    // Erro local
    return {
      success: false,
      message: error.message || defaultMessage,
      code: 0,
      details: error.stack || "Erro no processamento local"
    };
  },

  /**
   * Utilitário: Formata data para o backend
   * @param {Date|string} data - Data a ser formatada
   * @returns {string} Data no formato ISO ou null se inválida
   */
  formatarDataParaBackend(data) {
    try {
      if (!data) return null;
      const dateObj = typeof data === 'string' ? new Date(data) : data;
      
      if (isNaN(dateObj.getTime())) {
        throw new Error("Data inválida");
      }
      
      return dateObj.toISOString();
    } catch (error) {
      console.error("Erro ao formatar data:", error);
      return null;
    }
  },

  /**
   * Utilitário: Calcula dias entre datas
   * @param {string} inicio - Data de início
   * @param {string} [fim] - Data de fim (opcional, usa data atual se não informado)
   * @returns {number} Dias de diferença
   */
  calcularDiasEmAberto(inicio, fim = null) {
    if (!inicio) return 0;
    try {
      const inicioDate = new Date(inicio);
      const fimDate = fim ? new Date(fim) : new Date();
      
      if (isNaN(inicioDate.getTime()) || isNaN(fimDate.getTime())) {
        throw new Error("Data inválida");
      }
      
      const diff = fimDate - inicioDate;
      return Math.floor(diff / (1000 * 60 * 60 * 24));
    } catch (error) {
      console.error("Erro ao calcular dias:", error);
      return 0;
    }
  }
};